import React from "react"
import { twMerge } from "tailwind-merge"
import { AppResourceUnion } from "@love-irish/types"
import {
  GrammarReference,
  ipaGuide,
  IrishLanguageReference,
  PAGES,
} from "@love-irish/grammar-reference"
import Tooltip from "design_system/core/components/tooltip"

export interface PronunciationLabelGroupProps {
  /**
   * One pronunciation, or several comma-separated (e.g. "MAH-din WAH, MAW-jin"). Each is shown in the
   * codex "/…/" convention; multiples are joined with "or".
   */
  pronunciation?: string | null
  /**
   * IPA transcription, when the caller passes the two values directly instead of a whole `resource`
   * (e.g. a library list item whose GraphQL summary only carries the two scalar fields).
   */
  ipaPronunciation?: string | null
  className?: string
  /**
   * Optional guide affordance rendered as a muted "[ … ]" after the value(s) — typically a "?" that opens
   * a pronunciation / IPA cheat-sheet. The trigger itself is styled by the caller (usually foreground); the
   * brackets stay muted so the value reads as the emphasis.
   */
  guide?: React.ReactNode
  /** Any resource (codex record, library item, …); the group renders its pronunciation + IPA. */
  resource?: AppResourceUnion
  source?: "ipa" | "pronunciation"
}

/**
 * Phonetic pronunciation label. Splits a comma-separated value into multiple pronunciations, wraps each in
 * slashes, and joins them with "or" (e.g. /MAH-din WAH/ or /MAW-jin/). The slashes and brackets stay muted
 * while the value itself is darker, so a condensed one-line "/value/ [?]" reads at a glance. Renders nothing
 * when empty, so callers can drop it in unconditionally.
 */
export const PronunciationLabel: React.FC<PronunciationLabelGroupProps> = ({
  pronunciation,
  source,
  className,
  guide,
}) => {
  let parts = (pronunciation || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)

  if (parts.length === 0) return null

  const isIpa = source == "ipa"

  const grammarModal = isIpa ? (
    <GrammarReference
      entry={ipaGuide}
      className="cursor-help text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <Tooltip title="View guide">
        <strong>?</strong>
      </Tooltip>
    </GrammarReference>
  ) : (
    <IrishLanguageReference
      page={PAGES.PRONUNCIATION}
      className="cursor-help text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <Tooltip title="View guide">
        <strong>?</strong>
      </Tooltip>
    </IrishLanguageReference>
  )

  return (
    <div className={twMerge("text-xs group text-muted-foreground", className)}>
      {parts.map((part, i) => (
        <React.Fragment key={`${part}-${i}`}>
          {i > 0 && <span className="mx-1.5">or</span>}
          <div className="whitespace-nowrap text-secondary-300 inline-flex gap-1 items-center ">
            <div>
              <div className="text-[1.75em] inline-block -translate-y-[0.1em] align-middle">
                /{" "}
              </div>
            </div>
            <div>
              <Tooltip title={isIpa ? "IPA pronunciation" : "Pronunciation"}>
                <div className="cursor-help text-foreground text-highlight font-mono group-hover:bg-warning-200 group-hover:text-warning-900 transition-colors">
                  {part}
                </div>
              </Tooltip>
            </div>
            <div>
              <div className="text-[1.75em] inline-block -translate-y-[0.1em] align-middle">
                {" "}
                /
              </div>
            </div>
            {source == "ipa" && (
              <div className="inline-block text-muted-foreground">IPA</div>
            )}
          </div>
        </React.Fragment>
      ))}
      <p className="inline-block ml-1 align-super -translate-y-1/2 top-0 whitespace-nowrap">
        [{grammarModal}]
      </p>
    </div>
  )
}

/**
 * The written pronunciation AND the IPA on one condensed line, each in the "/value/ [?]" convention with
 * its own cheat-sheet guide. Pass a record via `resource`; renders nothing when neither a
 * written pronunciation nor an IPA is present, so callers can drop it in unconditionally. This is the
 * default export — reach for it whenever you show a record's pronunciation.
 */
export const PronunciationLabelGroup: React.FC<PronunciationLabelGroupProps> = ({
  resource,
  pronunciation,
  ipaPronunciation,
  className,
}) => {
  const written = resource?.pronunciation ?? pronunciation
  const ipa = resource?.ipaPronunciation ?? ipaPronunciation

  if (!written && !ipa) return null

  return (
    <div
      className={twMerge(
        "flex flex-wrap items-center gap-x-4 gap-y-1",
        className
      )}
    >
      {written && (
        <PronunciationLabel pronunciation={written} source="pronunciation" />
      )}
      {ipa && <PronunciationLabel pronunciation={ipa} source="ipa" />}
    </div>
  )
}

export default PronunciationLabelGroup
